import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BalanceGraph, AmortTable, buildAmortization } from './_finHelpers';

// Payment Calculator — Fixed Term / Fixed Payments tabs
export default function PaymentCalc() {
  const [tab, setTab] = useState('term');
  return (
    <CalcShell look="cnet" title="Payment Calculator" subtitle="Determine the monthly payment for a fixed-term loan, or the payoff time for a fixed monthly payment."
      about={<>
        <p>The <b>Payment Calculator</b> can determine the monthly payment amount or loan term for a fixed interest loan. Use the <b>Fixed Term</b> tab to calculate the monthly payment of a fixed-term loan. Use the <b>Fixed Payments</b> tab to calculate the time to pay off a loan with a fixed monthly payment.</p>
        <p>For car payment specifics, use the <b>Auto Loan Calculator</b>. To find net pay after tax deductions, use the <b>Take-Home-Paycheck Calculator</b>.</p>
      </>}
    >
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        <button onClick={()=>setTab('term')} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab==='term'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`} data-testid="pay-tab-term">Fixed Term</button>
        <button onClick={()=>setTab('payments')} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab==='payments'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`} data-testid="pay-tab-payments">Fixed Payments</button>
      </div>
      {tab === 'term' ? <FixedTerm/> : <FixedPayments/>}
    </CalcShell>
  );
}

function FixedTerm() {
  const [amount, setAmount] = useState(200000);
  const [years, setYears] = useState(15);
  const [months, setMonths] = useState(0);
  const [rate, setRate] = useState(6);
  const c = useMemo(() => {
    const n = years * 12 + months;
    const amort = buildAmortization(amount, rate, n);
    return { ...amort, n };
  }, [amount, years, months, rate]);
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Loan Amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1" data-testid="pay-amount"/></div></div>
          <div className="field"><label>Loan Term</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
              <div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
            </div>
          </div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Monthly Payment</div><div className="result-value" data-testid="pay-monthly">{fmt(c.M)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Total of {c.n} Payments</td><td className="text-right font-semibold">{fmt(c.totalPaid)}</td></tr>
            <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.totalInterest)}</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Principal',value:amount,color:'#2563eb'},{label:'Interest',value:c.totalInterest,color:'#10b981'}]} size={170}/></div>
        </div>
      </div>
      <p className="text-sm text-slate-600 mt-4">You will need to pay <b>{fmt(c.M)}</b> every month for {years} years and {months} months to pay off the debt.</p>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="calc-card p-5"><h3 className="font-bold text-slate-900 mb-3">Amortization Schedule</h3><AmortTable schedule={c}/></div>
        <div className="calc-card p-5"><h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3><BalanceGraph yearly={c.yearly} startBalance={amount}/></div>
      </div>
      <StepsPanel steps={[
        `Monthly rate r = ${rate}% / 12 = ${(rate/12).toFixed(4)}%`,
        `Number of payments n = ${c.n}`,
        `M = P·r(1+r)ⁿ / ((1+r)ⁿ − 1) = <b>${fmt(c.M)}</b>`,
      ]}/>
    </>
  );
}

function FixedPayments() {
  const [amount, setAmount] = useState(200000);
  const [monthly, setMonthly] = useState(1500);
  const [rate, setRate] = useState(6);
  const c = useMemo(() => {
    const r = rate/100/12;
    if (monthly <= amount * r) return { n: Infinity, years: 0, months: 0, totalPaid: 0, totalInterest: 0, yearly: [], amort: null };
    const n = -Math.log(1 - amount * r / monthly) / Math.log(1 + r);
    const nRounded = Math.ceil(n);
    const years = Math.floor(n / 12);
    const months = Math.round(n - years * 12);
    // Build amortization from actual PMT
    const amort = buildAmortization(amount, rate, nRounded);
    return { n: nRounded, years, months, totalPaid: monthly * n, totalInterest: monthly * n - amount, yearly: amort.yearly, M: monthly, schedule: amort };
  }, [amount, monthly, rate]);
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div data-print="inputs">
          <div className="field"><label>Loan Amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Monthly Pay</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={monthly} onChange={e=>setMonthly(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div data-print="result">
          <div className="result-panel mb-4"><div className="result-title">Payoff Time</div><div className="result-value">{isFinite(c.n) ? `${c.years} yr ${c.months} mo` : '—'}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Number of Payments</td><td className="text-right font-semibold">{isFinite(c.n) ? c.n : '∞'}</td></tr>
            <tr><td>Total Paid</td><td className="text-right">{fmt(c.totalPaid)}</td></tr>
            <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.totalInterest)}</td></tr>
          </tbody></table>
          {isFinite(c.n) && (
            <div className="mt-4">
              <PieChart parts={[
                { label: 'Principal', value: amount,           color: '#2563eb' },
                { label: 'Interest',  value: c.totalInterest,  color: '#10b981' },
              ]} size={170}/>
            </div>
          )}
        </div>
      </div>
      {isFinite(c.n) && c.yearly && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6" data-print="tables">
          <div className="calc-card p-5"><h3 className="font-bold text-slate-900 mb-3">Amortization Schedule</h3><AmortTable schedule={c.schedule}/></div>
          <div className="calc-card p-5"><h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3><BalanceGraph yearly={c.yearly} startBalance={amount}/></div>
        </div>
      )}
    </>
  );
}
